import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React from 'react';

import { useLogPosthogEvent } from '~src/commons/services/posthog-service';

import { SearchAddress, StateResponse } from '../../../api/api';
import { NoOptionsModal } from '../../category/no-options-modal';
import { GeneralError } from '../../commons/components/general-error';
import { apiInstance, useGlobalState } from '../../global-state';
import { InvalidPostalCodeModal } from './invalid-postal-code';

export function useAddressMutation() {
  const globalState = useGlobalState();

  const mutationFn = async (address: SearchAddress) => {
    const { data } = await apiInstance.v4.searchAddressPatch(globalState.checkout_session_id, {
      ...address,
      /**
       * Note: search_address.country can be null.
       */
      country: globalState.purchase_country,
    });
    return data;
  };

  return useMutation<StateResponse, AxiosError<{ message?: string }>, SearchAddress>({
    mutationFn,
    mutationKey: ['address'],
  });
}

interface AddressMutationErrorProps {
  error: AxiosError<{ message?: string }> | null;
  onClose: () => void;
  onSubmit: () => void;
}

export const AddressMutationError: React.FC<AddressMutationErrorProps> = ({
  error,
  onClose,
  onSubmit,
}) => {
  const logPosthogEvent = useLogPosthogEvent();
  const message = error?.response?.data?.message;

  const onCloseWithPosthogEvent = () => {
    onClose();
    logPosthogEvent('Address Change Canceled');
  };

  const onSubmitWithPosthogEvent = () => {
    onSubmit();
    logPosthogEvent('Provide New Address Button Clicked');
  };

  if (message === 'no shipping options') {
    return <NoOptionsModal onClose={onCloseWithPosthogEvent} onSubmit={onSubmitWithPosthogEvent} />;
  }

  if (message === 'invalid postal code') {
    return (
      <InvalidPostalCodeModal
        onClose={onCloseWithPosthogEvent}
        onSubmit={onSubmitWithPosthogEvent}
      />
    );
  }

  if (error) {
    // if none of previous errors -> display general one.
    logPosthogEvent('General Address Change Error Viewed');
    return <GeneralError error={error} onClose={onCloseWithPosthogEvent} />;
  }

  return null;
};
